import React, { useEffect, useState } from "react";
import { X, TrendingUp, Share2, Check } from "lucide-react";
import { api } from "../lib/api";
import { shareProgressCard } from "../lib/shareCard";

/**
 * Progress Notice popup — fires when the user's latest workout's working
 * tonnage exceeds the previous same-day_name workout's tonnage by >= 2.5%.
 * Independent of bulk / cut / maintenance phase. Pure positive reinforcement
 * for progressive overload doing its job.
 *
 * Once dismissed, the workout_id is persisted on the user so it doesn't
 * re-pop on subsequent dashboard visits.
 */
export default function ProgressNotice() {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [sharedFeedback, setSharedFeedback] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/progress/progress-notice");
                if (!cancelled && data?.show) setData(data);
            } catch (_) { /* non-blocking */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const close = async () => {
        setDismissed(true);
        if (data?.workout_id) {
            try { await api.post("/progress/progress-notice/dismiss", { workout_id: data.workout_id }); } catch (_) { /* ignore */ }
        }
    };

    const onShare = async () => {
        if (!data || sharing) return;
        setSharing(true);
        try {
            const res = await shareProgressCard({
                gainKg: data.tonnage_gain_kg,
                gainPct: data.gain_pct,
                dayName: data.day_name,
            });
            if (res.cancelled) {
                setSharedFeedback(null);
            } else if (res.shared) {
                setSharedFeedback("Shared");
            } else if (res.downloaded) {
                setSharedFeedback("Saved to device");
            }
        } catch (_) {
            setSharedFeedback("Couldn't generate card");
        } finally {
            setSharing(false);
            setTimeout(() => setSharedFeedback(null), 2200);
        }
    };

    if (!data || dismissed) return null;

    return (
        <div
            className="fixed inset-x-0 bottom-24 z-[60] mx-auto max-w-md px-3 sm:bottom-4"
            role="status"
            aria-live="polite"
            data-testid="progress-notice-popup"
        >
            <div className="relative border-2 border-emerald-500 bg-[#0a0a0a]/95 p-5 shadow-2xl backdrop-blur-md">
                <button
                    onClick={close}
                    aria-label="Dismiss"
                    data-testid="progress-notice-close"
                    className="absolute right-2 top-2 rounded-full p-1 text-white/55 hover:text-white"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="flex items-start gap-3">
                    <div className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md bg-emerald-500/15 text-emerald-400">
                        <TrendingUp className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="label-eyebrow text-emerald-400">The bar is moving</div>
                        <h3 className="mt-0.5 font-display text-lg font-black uppercase leading-tight text-white">
                            Up {data.tonnage_gain_kg} kg on {data.day_name}.
                        </h3>
                        <p className="mt-2 text-[13px] leading-relaxed text-white/90">
                            That&apos;s a <span className="font-bold text-emerald-400">{data.gain_pct}%</span> increase
                            over your previous {data.day_name} session. Tension is doing its job. When you hit the top
                            of every set with 1–2 RIR, add weight or reps next session and ride the curve.
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                            <button
                                onClick={onShare}
                                disabled={sharing}
                                data-testid="progress-notice-share"
                                className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-500 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-black hover:bg-emerald-400 disabled:opacity-60"
                            >
                                {sharedFeedback === "Shared" || sharedFeedback === "Saved to device" ? (
                                    <Check className="h-3.5 w-3.5" />
                                ) : (
                                    <Share2 className="h-3.5 w-3.5" />
                                )}
                                {sharing ? "Generating…" : (sharedFeedback || "Share my gain")}
                            </button>
                            <span className="text-[10px] uppercase tracking-wider text-white/40">
                                1080 × 1080 card
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
